import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Stack,
  Radio,
  RadioGroup,
  FormControlLabel,
  Checkbox,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Divider,
  IconButton,
  Tooltip,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  AlertTitle,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Paper,
  Grid,
  ButtonGroup,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Badge
} from "@mui/material";
import { Application } from "@microsoft/microsoft-graph-types";
import { Client } from "@microsoft/microsoft-graph-client";
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import InfoIcon from '@mui/icons-material/Info';
import SecurityIcon from '@mui/icons-material/Security';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import LockIcon from '@mui/icons-material/Lock';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';

interface ClientCredentialsManagerProps {
  open: boolean;
  onClose: () => void;
  app: Application;
  graphAccessToken: string;
  onSecretCreated?: (secretInfo: any) => void; 
  onCredentialDeleted?: () => void;
}

interface PasswordCredential {
  displayName?: string;
  endDateTime?: string;
  startDateTime?: string;
  keyId?: string;
  hint?: string;
}

interface KeyCredential {
  displayName?: string;
  endDateTime?: string;
  startDateTime?: string;
  keyId?: string;
  type?: string;
  usage?: string;
  thumbprint?: string;
}

interface ExpirationOption {
  label: string;
  value: number;
  recommended?: boolean;
}

const expirationOptions: ExpirationOption[] = [
  { label: "6 months", value: 6, recommended: true },
  { label: "1 year", value: 12, recommended: true },
  { label: "2 years", value: 24 },
  { label: "Custom", value: 0 }
];

const ClientCredentialsManager: React.FC<ClientCredentialsManagerProps> = ({
  open,
  onClose,
  app,
  graphAccessToken,
  onSecretCreated,
  onCredentialDeleted
}) => {
  // ... existing state variables remain the same ...
  const [credentialType, setCredentialType] = useState<"secret" | "certificate">("secret");
  const [displayName, setDisplayName] = useState<string>(""); 
  const [certBase64, setCertBase64] = useState<string>(""); 
  const [certFileName, setCertFileName] = useState<string>("");
  const [expirationOption, setExpirationOption] = useState<number>(12);
  const [customExpiration, setCustomExpiration] = useState<number>(30);
  const [passwords, setPasswords] = useState<PasswordCredential[]>([]);
  const [certs, setCerts] = useState<KeyCredential[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [deleteLoading, setDeleteLoading] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [successMessage, setSuccessMessage] = useState<string>("");
  const [newSecretValue, setNewSecretValue] = useState<string>("");

  // ... existing useEffect and functions remain the same ...
  useEffect(() => {
    if (!open || !app.id) return;
    setError("");
    setSuccessMessage("");
    setLoading(true);
    setNewSecretValue("");
    setDisplayName("");
    setCertBase64("");
    setCertFileName("");
    setExpirationOption(12);
    setCustomExpiration(30);
    fetchCredentials();
  }, [open, app.id, graphAccessToken]);

  const fetchCredentials = () => {
    if (!app.id) return;
    
    setLoading(true);
    const client = Client.init({
      authProvider: (done) => done(null, graphAccessToken)
    });

    client
      .api(`/applications/${app.id}?$select=passwordCredentials,keyCredentials`)
      .get()
      .then((res) => {
        setPasswords(res.passwordCredentials ?? []);
        setCerts(res.keyCredentials ?? []);
        setLoading(false);
      })
      .catch((err: any) => {
        console.error("Error fetching credentials:", err);
        setError(`Failed to list credentials: ${err.message}`);
        setLoading(false);
      });
  };

  const handleCertFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.length) return;
    const file = e.target.files[0];
    setCertFileName(file.name);

    try {
      const reader = new FileReader();
      reader.onload = (event: any) => {
        const base64Str = btoa(event.target.result); 
        setCertBase64(base64Str);
      };
      reader.readAsBinaryString(file);
    } catch (err: any) {
      console.error("Error reading cert file:", err);
      setError(`Could not read cert file: ${err.message}`);
    } finally {
      e.target.value = "";
    }
  };

  const getExpirationDate = () => {
    const now = new Date();
    let expirationDate = new Date(now);
    
    if (expirationOption === 0) {
      expirationDate.setDate(now.getDate() + customExpiration);
    } else {
      expirationDate.setMonth(now.getMonth() + expirationOption);
    }
    
    return expirationDate.toISOString();
  };

  const handleCreateCredential = async () => {
    if (!app.id) {
      alert("No application ID found.");
      return;
    }
    
    if (!displayName.trim()) {
      setError("Please provide a display name for your credential");
      return;
    }
    
    setError("");
    setSuccessMessage("");
    setNewSecretValue("");

    const client = Client.init({
      authProvider: (done) => done(null, graphAccessToken)
    });

    try {
      setLoading(true);
      const endDateTime = getExpirationDate();
      
      if (credentialType === "secret") {
        const body = { 
          passwordCredential: {
            displayName: displayName.trim(), 
            endDateTime 
          }
        };
        
        const resp = await client
          .api(`/applications/${app.id}/addPassword`)
          .post(body);
          
        setNewSecretValue(resp.secretText);
        setSuccessMessage(`Secret "${displayName}" created successfully`);
        onSecretCreated?.(resp);
      } else {
        if (!certBase64) {
          setError("No certificate file selected or it is empty.");
          setLoading(false);
          return;
        }
        
        const body = {
          keyCredential: {
            type: "AsymmetricX509Cert",
            usage: "Verify",
            key: certBase64,
            displayName: displayName.trim(),
            endDateTime
          }
        };
        
        const resp = await client
          .api(`/applications/${app.id}/addKey`)
          .post(body);
          
        setSuccessMessage(`Certificate "${displayName}" created successfully`);
      }

      fetchCredentials();
      setDisplayName("");
      setCertBase64("");
      setCertFileName("");
    } catch (err: any) {
      console.error("Error creating credential:", err);
      setError(`Failed to create credential: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCredential = async (keyId: string | undefined, isSecret: boolean) => {
    if (!keyId || !app.id) return;
    
    if (!window.confirm(`Are you sure you want to delete this ${isSecret ? 'secret' : 'certificate'}? This action cannot be undone.`)) {
      return;
    }
    
    setDeleteLoading(keyId);
    setError("");
    setSuccessMessage("");

    const client = Client.init({
      authProvider: (done) => done(null, graphAccessToken)
    });

    try {
      if (isSecret) {
        await client
          .api(`/applications/${app.id}/removePassword`)
          .post({ keyId });
          
        setSuccessMessage("Secret deleted successfully");
      } else {
        await client
          .api(`/applications/${app.id}/removeKey`)
          .post({ keyId });
          
        setSuccessMessage("Certificate deleted successfully");
      }

      fetchCredentials();
      onCredentialDeleted?.();
    } catch (err: any) {
      console.error(`Error deleting ${isSecret ? 'secret' : 'certificate'}:`, err);
      setError(`Failed to delete ${isSecret ? 'secret' : 'certificate'}: ${err.message}`);
    } finally {
      setDeleteLoading("");
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const formatDate = (isoDate?: string) => {
    if (!isoDate) return 'N/A';
    const date = new Date(isoDate);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  const isExpired = (endDate?: string) => {
    if (!endDate) return false;
    return new Date(endDate) < new Date();
  };

  const getDaysRemaining = (endDate?: string) => {
    if (!endDate) return 'N/A';
    const end = new Date(endDate);
    const now = new Date();
    if (end < now) return 'Expired';
    
    const diffTime = end.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return `${diffDays} days`;
  };

  const getStatusChip = (endDate?: string) => {
    if (!endDate) return <Chip label="Unknown" size="small" />;
    
    const end = new Date(endDate);
    const now = new Date();
    const diffTime = end.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      return <Chip 
        label="Expired" 
        size="small" 
        color="error" 
        icon={<ErrorIcon />}
      />;
    } else if (diffDays <= 30) {
      return <Chip 
        label={`${diffDays} days`} 
        size="small" 
        color="warning" 
        icon={<WarningIcon />}
      />;
    } else {
      return <Chip 
        label={`${diffDays} days`} 
        size="small" 
        color="success" 
        icon={<CheckCircleIcon />}
      />;
    }
  };

  const totalCredentials = passwords.length + certs.length;
  const expiredCredentials = passwords.filter(p => isExpired(p.endDateTime)).length + 
                           certs.filter(c => isExpired(c.endDateTime)).length;

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: { 
          borderRadius: 3,
          boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
        }
      }}
    >
      <DialogTitle sx={{ 
        background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
        color: 'white',
        pb: 3
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <SecurityIcon sx={{ fontSize: 32 }} />
          <Box>
            <Typography variant="h5" component="div" sx={{ fontWeight: 600 }}>
              Client Credentials Manager
            </Typography>
            <Typography variant="subtitle1" sx={{ opacity: 0.9, mt: 0.5 }}>
              {app.displayName}
            </Typography>
          </Box>
          <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
            <Chip 
              label={`${totalCredentials} Total`} 
              sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
            />
            {expiredCredentials > 0 && (
              <Chip 
                label={`${expiredCredentials} Expired`} 
                color="error"
                sx={{ bgcolor: 'error.dark' }}
              />
            )}
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
            <CircularProgress size={40} />
            <Typography sx={{ ml: 2 }}>Loading credentials...</Typography>
          </Box>
        ) : (
          <Box>
            {/* Status Messages */}
            {error && (
              <Alert severity="error" sx={{ m: 3, mb: 2 }}>
                <AlertTitle>Error</AlertTitle>
                {error}
              </Alert>
            )}

            {successMessage && (
              <Alert severity="success" sx={{ m: 3, mb: 2 }}>
                {successMessage}
              </Alert>
            )}

            {/* New Secret Value Display */}
            {newSecretValue && (
              <Card sx={{ m: 3, mb: 2, border: '2px solid', borderColor: 'warning.main' }}>
                <CardHeader
                  avatar={<LockIcon color="warning" />}
                  title="⚠️ Copy Secret Value Now!"
                  titleTypographyProps={{ fontWeight: 600, color: 'warning.dark' }}
                  subheader="This value will not be shown again"
                />
                <CardContent>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                      value={newSecretValue}
                      fullWidth
                      variant="outlined"
                      InputProps={{ 
                        readOnly: true,
                        sx: { fontFamily: 'monospace', fontSize: '0.9rem' }
                      }}
                    />
                    <Tooltip title="Copy to clipboard">
                      <IconButton 
                        onClick={() => copyToClipboard(newSecretValue)}
                        color="primary"
                        sx={{ bgcolor: 'primary.light', color: 'white' }}
                      >
                        <ContentCopyIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </CardContent>
              </Card>
            )}

            <Grid container spacing={3} sx={{ p: 3 }}>
              {/* Existing Secrets */}
              <Grid item xs={12} md={6}>
                <Card sx={{ height: '100%' }}>
                  <CardHeader
                    avatar={
                      <Badge badgeContent={passwords.length} color="primary">
                        <VpnKeyIcon color="primary" />
                      </Badge>
                    }
                    title="Client Secrets"
                    titleTypographyProps={{ fontWeight: 600 }}
                    action={
                      <Chip 
                        label={`${passwords.filter(p => !isExpired(p.endDateTime)).length} Active`} 
                        color="success" 
                        size="small" 
                      />
                    }
                  />
                  <CardContent sx={{ pt: 0 }}>
                    {passwords.length === 0 ? (
                      <Box sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>
                        <VpnKeyIcon sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} />
                        <Typography>No client secrets found</Typography>
                      </Box>
                    ) : (
                      <Stack spacing={2}>
                        {passwords.map((pw) => (
                          <Paper 
                            key={pw.keyId}
                            sx={{ 
                              p: 2, 
                              border: '1px solid',
                              borderColor: isExpired(pw.endDateTime) ? 'error.main' : 'divider',
                              borderRadius: 2
                            }}
                          >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                {pw.displayName || "(no displayName)"}
                              </Typography>
                              {getStatusChip(pw.endDateTime)}
                            </Box>
                            
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                              <Typography variant="caption" color="text.secondary">
                                Key ID: {pw.keyId?.substring(0, 8)}...
                              </Typography>
                              <Tooltip title="Copy full key ID">
                                <IconButton 
                                  size="small" 
                                  onClick={() => copyToClipboard(pw.keyId || '')}
                                >
                                  <ContentCopyIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>

                            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <CalendarTodayIcon fontSize="small" />
                              Expires: {formatDate(pw.endDateTime)}
                            </Typography>

                            <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
                              <Tooltip title="Delete secret">
                                <IconButton 
                                  color="error" 
                                  size="small"
                                  onClick={() => handleDeleteCredential(pw.keyId, true)}
                                  disabled={!!deleteLoading}
                                >
                                  {deleteLoading === pw.keyId ? 
                                    <CircularProgress size={16} /> : 
                                    <DeleteIcon fontSize="small" />
                                  }
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </Paper>
                        ))}
                      </Stack>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* Existing Certificates */}
              <Grid item xs={12} md={6}>
                <Card sx={{ height: '100%' }}>
                  <CardHeader
                    avatar={
                      <Badge badgeContent={certs.length} color="primary">
                        <VerifiedUserIcon color="primary" />
                      </Badge>
                    }
                    title="Certificates"
                    titleTypographyProps={{ fontWeight: 600 }}
                    action={
                      <Chip 
                        label={`${certs.filter(c => !isExpired(c.endDateTime)).length} Active`} 
                        color="success" 
                        size="small" 
                      />
                    }
                  />
                  <CardContent sx={{ pt: 0 }}>
                    {certs.length === 0 ? (
                      <Box sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>
                        <VerifiedUserIcon sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} />
                        <Typography>No certificates found</Typography>
                      </Box>
                    ) : (
                      <Stack spacing={2}>
                        {certs.map((cert) => (
                          <Paper 
                            key={cert.keyId}
                            sx={{ 
                              p: 2, 
                              border: '1px solid',
                              borderColor: isExpired(cert.endDateTime) ? 'error.main' : 'divider',
                              borderRadius: 2
                            }}
                          >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                {cert.displayName || "(no displayName)"}
                              </Typography>
                              {getStatusChip(cert.endDateTime)}
                            </Box>
                            
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                              <Typography variant="caption" color="text.secondary">
                                Thumbprint: {cert.thumbprint?.substring(0, 8)}...
                              </Typography>
                              <Tooltip title="Copy thumbprint">
                                <IconButton 
                                  size="small" 
                                  onClick={() => copyToClipboard(cert.thumbprint || '')}
                                >
                                  <ContentCopyIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>

                            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <CalendarTodayIcon fontSize="small" />
                              Expires: {formatDate(cert.endDateTime)}
                            </Typography>

                            <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
                              <Tooltip title="Delete certificate">
                                <IconButton 
                                  color="error" 
                                  size="small"
                                  onClick={() => handleDeleteCredential(cert.keyId, false)}
                                  disabled={!!deleteLoading}
                                >
                                  {deleteLoading === cert.keyId ? 
                                    <CircularProgress size={16} /> : 
                                    <DeleteIcon fontSize="small" />
                                  }
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </Paper>
                        ))}
                      </Stack>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            {/* Create New Credential */}
            <Box sx={{ p: 3 }}>
              <Accordion>
                <AccordionSummary 
                  expandIcon={<ExpandMoreIcon />}
                  sx={{ 
                    bgcolor: 'primary.light', 
                    color: 'primary.contrastText',
                    borderRadius: 1,
                    '&:hover': { bgcolor: 'primary.main' }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AddIcon />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Create New Credential
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 3 }}>
                  <Alert severity="info" sx={{ mb: 3 }}>
                    <AlertTitle>Security Best Practices</AlertTitle>
                    <Box component="ul" sx={{ m: 0, pl: 2 }}>
                      <li>Use descriptive names to easily identify credentials</li>
                      <li>Rotate secrets regularly (recommended: 6-12 months)</li>
                      <li>Use certificates for higher security when possible</li>
                      <li>Store secrets securely in Azure Key Vault or similar service</li>
                    </Box>
                  </Alert>

                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Credential Type
                    </Typography>
                    <ButtonGroup variant="outlined" sx={{ mb: 2 }}>
                      <Button
                        variant={credentialType === 'secret' ? 'contained' : 'outlined'}
                        startIcon={<VpnKeyIcon />}
                        onClick={() => setCredentialType('secret')}
                      >
                        Client Secret
                      </Button>
                      <Button
                        variant={credentialType === 'certificate' ? 'contained' : 'outlined'}
                        startIcon={<VerifiedUserIcon />}
                        onClick={() => setCredentialType('certificate')}
                      >
                        Certificate
                      </Button>
                    </ButtonGroup>
                  </Box>

                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Display Name / Description"
                        value={displayName}
                        onChange={(e) => setDisplayName(e.target.value)}
                        fullWidth
                        placeholder="e.g., Production API Access, Dev Environment"
                        helperText="A meaningful name helps identify the purpose"
                        required
                        variant="outlined"
                      />
                    </Grid>
                    
                    <Grid item xs={12} md={3}>
                      <FormControl fullWidth>
                        <InputLabel>Expires In</InputLabel>
                        <Select
                          value={expirationOption}
                          label="Expires In"
                          onChange={(e) => setExpirationOption(Number(e.target.value))}
                        >
                          {expirationOptions.map(option => (
                            <MenuItem key={option.value} value={option.value}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {option.label}
                                {option.recommended && 
                                  <Chip label="Recommended" size="small" color="success" />
                                }
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    {expirationOption === 0 && (
                      <Grid item xs={12} md={3}>
                        <TextField
                          label="Custom Days"
                          type="number"
                          value={customExpiration}
                          onChange={(e) => setCustomExpiration(Number(e.target.value))}
                          InputProps={{ inputProps: { min: 1, max: 730 } }}
                          helperText="1-730 days"
                          fullWidth
                        />
                      </Grid>
                    )}
                  </Grid>

                  {credentialType === "certificate" && (
                    <Box sx={{ mt: 3 }}>
                      <Paper 
                        sx={{ 
                          p: 3, 
                          border: '2px dashed', 
                          borderColor: 'primary.main',
                          textAlign: 'center',
                          cursor: 'pointer',
                          '&:hover': { bgcolor: 'primary.light', opacity: 0.1 }
                        }}
                      >
                        <Button
                          variant="outlined"
                          component="label"
                          startIcon={<VerifiedUserIcon />}
                          size="large"
                        >
                          Upload Certificate (PEM, CER, CRT)
                          <input
                            type="file"
                            hidden
                            onChange={handleCertFileChange}
                            accept=".pem,.cer,.crt"
                          />
                        </Button>
                        {certFileName && (
                          <Typography variant="body2" sx={{ mt: 1, color: 'success.main' }}>
                            ✓ Selected: {certFileName}
                          </Typography>
                        )}
                      </Paper>
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, bgcolor: 'grey.50' }}>
        <Button onClick={onClose} size="large">
          Close
        </Button>
        <Button 
          variant="contained" 
          onClick={handleCreateCredential}
          disabled={
            loading || 
            (credentialType === "certificate" && !certBase64) ||
            !displayName.trim()
          }
          startIcon={loading ? <CircularProgress size={16} /> : <AddIcon />}
          size="large"
          sx={{ minWidth: 160 }}
        >
          {loading ? 'Creating...' : `Create ${credentialType === "secret" ? "Secret" : "Certificate"}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ClientCredentialsManager;